import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { RELEASE_PRODUCT_LABELS, ageLabel, formatDate, productErrorMessage, runningVersionView } from "../../lib/product";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

const TONES = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

function VersionCard({ entry, registering, onRegister }) {
  const view = runningVersionView(entry);
  const live = entry.live;
  const sha = live && live.git_sha && live.git_sha !== "unknown" ? live.git_sha.slice(0, 7) : "";
  const age = live ? ageLabel(live.build_date) : "";
  const built = live ? formatDate(live.build_date) : "";
  return (
    <Card className="p-4 space-y-2" data-testid={`running-${entry.product}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-slate-600">{RELEASE_PRODUCT_LABELS[entry.product]}</div>
        {view.badge && (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[view.tone]}`} data-testid={`running-${entry.product}-badge`}>
            {view.badge}
          </span>
        )}
      </div>
      {live && entry.reading === "ok" ? (
        <>
          <div className="font-display font-bold text-2xl text-slate-900 tabular-nums" data-testid={`running-${entry.product}-version`}>{live.version}</div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
            {sha && <span>Commit <span className="font-mono">{sha}</span></span>}
            {built && <span>Construite le {built}{age ? ` (${age})` : ""}</span>}
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-400" data-testid={`running-${entry.product}-none`}>Version inconnue</div>
      )}
      {view.note && <p className="text-xs text-slate-600">{view.note}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <div className="text-xs text-slate-500 min-w-0">
          {entry.recorded ? (
            <Link to={`/produit/versions/${entry.recorded.id}`} className="text-jamora hover:underline" data-testid={`running-${entry.product}-release`}>
              Voir la version {entry.recorded.label}{entry.recorded.has_notes ? " et ses nouveautés" : ""}
            </Link>
          ) : entry.latest ? (
            <span>Dernière version enregistrée : <Link to={`/produit/versions/${entry.latest.id}`} className="text-jamora hover:underline">{entry.latest.label}</Link></span>
          ) : <span>Aucune version enregistrée</span>}
        </div>
        {entry.can_register && (
          <Button size="sm" variant="outline" onClick={() => onRegister(entry.product)} disabled={registering} data-testid={`running-${entry.product}-register`}>
            {registering ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Enregistrer cette version
          </Button>
        )}
      </div>
    </Card>
  );
}

// « En production aujourd'hui » (lot 29, V4): what each product runs now, read by the server, against the recorded versions.
// The reading itself lives in `useRunningVersions`, shared with the list and the release page.
export default function RunningVersions({ running, onRegistered }) {
  const { products, phase, refreshing, reload } = running;
  const [registering, setRegistering] = useState("");

  const register = async (product) => {
    if (registering) return;
    setRegistering(product);
    try {
      const { data } = await api.post(`/product/versions/${product}/register`);
      toast.success(data.status === "recorded" ? "Version enregistrée" : "Cette version était déjà enregistrée");
      await reload(true);
      onRegistered?.();
    } catch (e) {
      toast.error(productErrorMessage(e, "Impossible d'enregistrer cette version."));
    } finally {
      setRegistering("");
    }
  };

  if (phase === "loading") return <div className="grid gap-3 md:grid-cols-2" data-testid="running-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-28 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-4 flex flex-wrap items-center justify-between gap-2" data-testid="running-error">
        <p className="text-sm text-slate-700">Impossible de lire les versions en production.</p>
        <Button variant="outline" size="sm" onClick={() => reload()}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }
  return (
    <section className="space-y-2" aria-label="En production aujourd'hui" data-testid="running-versions">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">En production aujourd'hui</h2>
        <Button variant="ghost" size="sm" className="h-8 text-slate-600" onClick={() => reload(true)} disabled={refreshing} data-testid="running-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />Actualiser
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {products.map((entry) => <VersionCard key={entry.product} entry={entry} registering={registering === entry.product} onRegister={register} />)}
      </div>
    </section>
  );
}
